import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CloseCircleGreyIcon } from 'assets';
import {
  Breadcrumbs,
  Button,
  SkillsComparisonChart,
  SkillData,
  CandidateSelectionModal,
  CandidateCard,
  MultiCandidateComparison,
  Spinner,
} from 'components';
import { RootReducerState } from 'state/reducers';
import { resetCandidatesComparison, setCandidates, setPrimaryCandidate, toggleComparisonCandidate } from 'state/actions/candidatesComparison';
import { useForm } from 'react-hook-form';
import { useCompareCandidates, useMobile, useReviewTalentsCandidates } from 'hooks';
import { useReportDetails } from 'hooks/useReportDetails';
import { useFetchDecisionBuckets } from 'hooks/useFetchDecisionBuckets';
import { useUpdateCandidateDecision, UpdateDecisionType } from 'hooks/useUpdateCandidateDecision';
import { useComparisonCacheStore } from 'state/zustand/comparisonCache';
import { getScoreColor } from 'utils/scoreColor';

const CompareCandidatesPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: talentId } = useParams<{ id: string; id2: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useMobile({ size: 640 });
  const [isOpen, setIsOpen] = useState(false);
  const [isRestoringFromUrl, setIsRestoringFromUrl] = useState(false);
  const clearComparisonCache = useComparisonCacheStore((s) => s.clearCache);

  // Clear cache when navigating away from the comparison page
  useEffect(() => {
    return () => {
      clearComparisonCache();
    };
  }, [clearComparisonCache]);
  
  // Fetch all candidates for this assessment
  const { candidates: apiCandidates, isLoading: isFetchingCandidates } = useReviewTalentsCandidates({
    assessmentId: talentId,
    pageSize: 100,
  });
  
  const { candidates, primaryCandidateId, comparisonCandidateIds } = useSelector(
    (s: RootReducerState) => (s as any)?.candidatesComparison || {
      candidates: [],
      primaryCandidateId: null,
      comparisonCandidateIds: []
    }
  );

  const primary = candidates.find((c: any) => c.id === primaryCandidateId) || null;
  const others = candidates.filter((c: any) => comparisonCandidateIds.includes(c.id));

  // Fetch report details for each candidate — same source as the report page
  const { reportDetails: primaryReport, isLoading: primaryReportLoading } = useReportDetails(primaryCandidateId || undefined);
  const { reportDetails: compReport, isLoading: compReportLoading } = useReportDetails(comparisonCandidateIds[0] || undefined);

  // Fetch decision bucket options — same source as the report page
  const { data: decisionBucketsData } = useFetchDecisionBuckets(talentId || undefined);
  const decisionOptions = (decisionBucketsData?.decision_buckets || []).map(b => ({
    label: b.decision,
    value: b.decision,
  }));

  const methods = useForm();

  // Sync report bucket into form when data arrives — mirrors the report page useEffect
  useEffect(() => {
    if (primaryReport?.response_details?.bucket) {
      methods.setValue('primaryDecision', primaryReport.response_details.bucket);
    }
  }, [primaryReport?.response_details?.bucket]);

  useEffect(() => {
    if (compReport?.response_details?.bucket && comparisonCandidateIds[0]) {
      methods.setValue(`comparisonDecisions.${comparisonCandidateIds[0]}`, compReport.response_details.bucket);
    }
  }, [compReport?.response_details?.bucket, comparisonCandidateIds[0]]);

  const primaryDecisionValue = methods.watch('primaryDecision');
  const comparisonDecisionValue = methods.watch(`comparisonDecisions.${comparisonCandidateIds[0]}`);

  const { updateDecision } = useUpdateCandidateDecision();

  const handleDecisionChange = (candidateId: string, value: string) => {
    const report = candidateId === primaryCandidateId ? primaryReport : compReport;
    if (!report?.response_details?.id) return;

    const decisionMap: Record<string, string> = {
      'Interview': 'interview',
      'Revisit': 'revisit',
      'Probe further': 'probe_further',
      'Request More Info': 'request_more_info',
      'Save for future': 'save_for_future',
      'Reject': 'reject',
    };
    const apiDecision = decisionMap[value] || value.toLowerCase().replace(/\s+/g, '_');
    updateDecision({
      assessment_response_ids: [report.response_details.id],
      decision: apiDecision as UpdateDecisionType,
    });
  };

  // Build candidate IDs from Redux state or URL params (for refresh persistence)
  const selectedIdsNumeric = useMemo(() => {
    // Try Redux state first
    const reduxIds = [primaryCandidateId, ...comparisonCandidateIds].filter(Boolean) as string[];
    if (reduxIds.length >= 2) {
      return reduxIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
    }
    // Fall back to URL params (on refresh, Redux is empty but URL persists)
    const primaryFromUrl = searchParams.get('primary');
    const compareFromUrl = searchParams.get('compare');
    if (primaryFromUrl && compareFromUrl) {
      const urlIds = [primaryFromUrl, ...compareFromUrl.split(',').filter(Boolean)];
      return urlIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n));
    }
    return [];
  }, [primaryCandidateId, comparisonCandidateIds, searchParams]);

  // Lock the IDs sent to the comparison API — only update when candidates are added,
  // never when one is removed, so client-side filtering avoids a credit-costing refetch.
  const [comparisonFetchIds, setComparisonFetchIds] = useState<number[]>([]);
  useEffect(() => {
    if (selectedIdsNumeric.length < 2) return;
    const hasNewId = selectedIdsNumeric.some(id => !comparisonFetchIds.includes(id));
    if (hasNewId) setComparisonFetchIds(selectedIdsNumeric);
  }, [selectedIdsNumeric]);

  // Hook for v2 comparison — uses query caching so refreshes don't re-charge credits
  const { data, isLoading, error } = useCompareCandidates(comparisonFetchIds);

  const scoreToColor = getScoreColor;

  const scoreToLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Average';
    return 'Needs Growth';
  };

  // Restore state from URL parameters on mount
  useEffect(() => {
    const primaryFromUrl = searchParams.get('primary');
    const compareFromUrl = searchParams.get('compare');
    
    if (primaryFromUrl && compareFromUrl && apiCandidates.length > 0 && !isRestoringFromUrl) {
      setIsRestoringFromUrl(true);
      
      // Transform API candidates (include bucket for decision seeding)
      const transformedCandidates = apiCandidates.map((candidate) => ({
        id: String(candidate.id),
        name: candidate.full_name,
        score: candidate.total_score ? parseFloat(candidate.total_score) : 0,
        email: candidate.email,
        phone_number: candidate.phone_number,
        created_at: candidate.created_at,
        portfolio: candidate.portfolio,
        resume: candidate.resume,
        bucket: candidate.bucket,
      }));
      
      // Restore Redux state from URL
      dispatch(setCandidates(transformedCandidates));
      dispatch(setPrimaryCandidate(primaryFromUrl));
      
      const compareIds = compareFromUrl.split(',').filter(Boolean);
      compareIds.forEach(id => dispatch(toggleComparisonCandidate(id)));
    }
  }, [searchParams, apiCandidates, dispatch, isRestoringFromUrl]);

  // Sync URL params with selected candidates
  useEffect(() => {
    if (primaryCandidateId && comparisonCandidateIds.length > 0) {
      const params = new URLSearchParams();
      params.set('primary', primaryCandidateId);
      params.set('compare', comparisonCandidateIds.join(','));
      setSearchParams(params, { replace: true });
    }
  }, [primaryCandidateId, comparisonCandidateIds, setSearchParams]);

  const comparisonData: SkillData[] = useMemo(() => {
    const payload = data?.data;
    if (!payload || !primary || others.length === 0) return [];
    const primaryId = parseInt(primary.id, 10);
    const otherId = parseInt(others[0].id, 10);
    return payload.comparisons.map((cat) => {
      const p = cat.candidates.find((c) => c.candidate_id === primaryId)?.score ?? 0;
      const o = cat.candidates.find((c) => c.candidate_id === otherId)?.score ?? 0;
      return {
        name: cat.category,
        expectation: p,
        result: o,
        resultLabel: scoreToLabel(o),
        color: scoreToColor(o),
      } as SkillData;
    });
  }, [data, primary, others]);

  // Build multi-candidate comparison sections from API data, filtered to active candidates only.
  // Because comparisonFetchIds never shrinks, removing a candidate just hides their column
  // without triggering a new API call.
  const multiCandidateComparisonData = useMemo(() => {
    const payload = data?.data;
    if (!payload) return [] as any[];
    const activeIds = new Set([primaryCandidateId, ...comparisonCandidateIds]);
    const idToName = new Map<number, string>(
      payload.candidates.map((c) => [c.id, c.profile.name])
    );
    return payload.comparisons.map((cat) => ({
      title: cat.category,
      candidates: cat.candidates
        .filter((c) => activeIds.has(String(c.candidate_id)))
        .map((c) => ({
          id: String(c.candidate_id),
          name: idToName.get(c.candidate_id) || `Candidate ${c.candidate_id}`,
          score: c.score,
          color: scoreToColor(c.score),
        })),
    }));
  }, [data, primaryCandidateId, comparisonCandidateIds]);

  const handleCancelComparison = () => {
    dispatch(resetCandidatesComparison());
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDone = (selectedCandidate: string) => {
    setIsOpen(false);
  };

  const handleViewReport = (candidateId: string) => {
    if (talentId && candidateId) {
      window.open(`/employer/manage-jobs/review-talent/${talentId}/report/${candidateId}`, '_blank');
    }
  };

  const handleRemoveCandidate = (candidateId: string) => {
    if (candidateId === primaryCandidateId) return;
    dispatch(toggleComparisonCandidate(candidateId));
  };

  const getDisplayName = (fullName: string) => {
    if (isMobile) {
      return fullName.split(' ')[0]; // Return only first name on mobile
    }
    return fullName; // Return full name on desktop
  };

  const isMultipleCandidates = others.length > 1;

  if (isLoading || isFetchingCandidates || primaryReportLoading || compReportLoading) {
    return (
      <div className="p-0 overflow-hidden h-[100dvh] w-full">
        <main className='md:px-9 px-4 md:py-5 py-6 overflow-auto h-[calc(100dvh-78px-var(--credit-banner-h,0px))] bg-[#F9FAFB] scrollbar-none flex flex-col items-center justify-center'>
          <Spinner />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-0 overflow-hidden h-[100dvh] w-full">
        <main className='md:px-9 px-4 md:py-5 py-6 overflow-auto h-[calc(100dvh-78px-var(--credit-banner-h,0px))] bg-[#F9FAFB] scrollbar-none'>
          <Breadcrumbs />
          <h1 className="mt-4 text-2xl font-semibold text-[#012169] mb-6">Candidate Comparison</h1>
          <p className="text-sm text-[#E74444]">{(error as any)?.message || 'Failed to fetch comparison'}</p>
        </main>
      </div>
    );
  }

  if (!primary) {
    return (
      <div className="p-0 overflow-hidden h-[100dvh] w-full">
        <main className='md:px-9 px-4 md:py-5 py-6 overflow-auto h-[calc(100dvh-78px-var(--credit-banner-h,0px))] bg-[#F9FAFB] scrollbar-none'>
          <Breadcrumbs />
          <h1 className="mt-4 text-2xl font-semibold text-[#012169] mb-6">Candidate Comparison</h1>
          <p className="text-sm text-[#475467]">No candidates selected. Go back and pick candidates.</p>
        </main>
      </div>
    );
  }

  // Render multi-candidate view when more than 1 comparison candidate
  if (isMultipleCandidates) {
    return (
      <div className="p-0 overflow-hidden h-[100dvh] w-full">
        <CandidateSelectionModal isOpen={isOpen} onClose={handleClose} onDone={handleDone} />

        <main className='md:py-5 py-6 overflow-auto h-[calc(100dvh-78px-var(--credit-banner-h,0px))] bg-[#F9FAFB] scrollbar-none'>
          <div className='md:px-12 px-6'><Breadcrumbs /></div>

          <div className="flex justify-between items-center sm:mt-10 mt-6 md:mb-8 mb-5 md:px-12 px-6">
            <h1 className="sm:text-3xl text-2xl font-semibold text-[#012169]">Comparing</h1>
            <Button
              type="outline"
              onClick={handleCancelComparison}
              className="!border-[#98A2B3] !md:border !border-none rounded-[3px] !text-[#344054] !md:bg-white !bg-transparent !h-fit !sm:py-2 !min-h-fit mt-0 !ml-0 w-fit px-8"
            >
              <span className='md:flex hidden'>Cancel Comparison</span>
              <CloseCircleGreyIcon className='md:hidden flex' />
            </Button>
          </div>

          <MultiCandidateComparison
            sections={multiCandidateComparisonData}
            assessmentId={talentId}
            primaryCandidateId={primaryCandidateId || undefined}
            onViewReport={handleViewReport}
            onRemoveCandidate={handleRemoveCandidate}
          />
        </main>

      </div>
    );
  }

  // Original two-candidate comparison view
  return (
    <div className="p-0 overflow-hidden h-[100dvh] w-full">
      <CandidateSelectionModal isOpen={isOpen} onClose={handleClose} onDone={handleDone} />

      <main className='md:py-5 py-6 overflow-auto h-[calc(100dvh-78px-var(--credit-banner-h,0px))] bg-[#F9FAFB] scrollbar-none'>
        <div className='md:px-12 px-6'><Breadcrumbs /></div>

        <div className="flex justify-between items-center sm:mt-10 mt-6 md:mb-8 mb-5 md:px-12 px-6">
          <h1 className="sm:text-3xl text-2xl font-semibold text-[#012169]">Comparing</h1>
          <Button
            type="outline"
            onClick={handleCancelComparison}
            className="!border-[#98A2B3] !md:border !border-none rounded-[3px] !text-[#344054] !md:bg-white !bg-transparent !h-fit !sm:py-2 !min-h-fit mt-0 !ml-0 w-fit px-8"
          >
            <span className='md:flex hidden'>Cancel Comparison</span>
            <CloseCircleGreyIcon className='md:hidden flex' />
          </Button>
        </div>

        <div className="grid xl:grid-cols-[0.4fr_1.5fr_0.4fr] gap-8 bg-white rounded-lg sm:py-2.5 md:px-12 px-6 overflow-y-auto">
          <CandidateCard
            candidate={primary}
            onSwitchCandidate={() => setIsOpen(true)}
            register={methods.register}
            formFieldName="primaryDecision"
            decisionValue={primaryDecisionValue}
            decisionOptions={decisionOptions}
            onDecisionChange={(decision) => handleDecisionChange(primary.id, decision)}
          />

          <div className='overflow-y-auto min-h-[90dvh]'>
            {comparisonData.length > 0 && (
              <SkillsComparisonChart
                expectationLabel={getDisplayName(primary.name)}
                resultLabel={getDisplayName(others[0].name)}
                skills={comparisonData}
                maxValue={100}
                showPercentages={true}
                className='!sm:mt-10 !mt-5'
              />
            )}
            <div className="bg-[#F5FAFF] border border-[#D1E9FF] rounded-lg p-6 mt-8">
              <div className="flex items-center gap-2 mb-4 bg-[#E6F3FF] py-1 px-3 cursor-pointer w-fit rounded-md">
                <h3 className="text-lg font-semibold text-[#012169]">AI opinion</h3>
                <span className="text-yellow-500">✨</span>
              </div>

              <div className="space-y-4 text-sm text-[#344054]">
                {data?.data?.ai_opinion?.comparison && (
                  <p>{data?.data?.ai_opinion?.comparison}</p>
                )}
                {data?.data?.ai_opinion?.recommendation && (
                  <p><strong>Recommendation:</strong> {data?.data?.ai_opinion?.recommendation}</p>
                )}
                {!data?.data?.ai_opinion?.comparison && !data?.data?.ai_opinion?.recommendation && (
                  <p>No AI opinion available for this comparison.</p>
                )}
              </div>
            </div>
          </div>

          {others.length > 0 && (
            <CandidateCard
              candidate={others[0]}
              onSwitchCandidate={() => setIsOpen(true)}
              register={methods.register}
              formFieldName={`comparisonDecisions.${others[0].id}`}
              decisionValue={comparisonDecisionValue}
              decisionOptions={decisionOptions}
              onDecisionChange={(decision) => handleDecisionChange(others[0].id, decision)}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export { CompareCandidatesPage };

<style>
  {`
  .radioButton {
    label {
        div {
            input {
                &:checked {
                    &::after {
                        width: 9px !important;
                        height: 9px !important;
                    }
                }

            }
        }
    }
}
  `}
</style>